import { Box, Button, FormLabel, useToast } from '@chakra-ui/react';
import { PeOrderOperationType } from 'enums/modules/pe/peOperationType';
import { Form, Formik } from 'formik';
import useFormError from 'hooks/shared/useFormError';
import AppCard from 'presentation/components/shared/card/AppCard';
import FormNumberInput from 'presentation/components/shared/form/FormNumberInput';
import GridHeading from 'presentation/components/shared/grid/GridHeading';
import ButtonTabs from 'presentation/components/shared/tabs/ButtonTabs';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import AppRoute from 'router/AppRoute';
import { useCreatePeCompanyOrderMutation } from 'rtkQuery/sharespro/queries/peService';
import * as Yup from 'yup';

interface FormValues {
  quantity: string;
  operation: PeOrderOperationType;
  requested_price: string;
  sum: string;
}

interface Props {
  quantity?: number;
  operationType?: PeOrderOperationType;
  requestedPrice?: number;
  companyCurrency?: string;
  companyName: string;
  companyId: ID;
}

const CreatePeCompanyOrderForm: FC<Props> = ({
  quantity,
  operationType,
  requestedPrice,
  companyId,
  companyName,
  companyCurrency,
}) => {
  const formErrorHandler = useFormError<FormValues>();

  const { t } = useTranslation();

  const toast = useToast();

  const navigate = useNavigate();

  const [createPeOrder, { isLoading }] = useCreatePeCompanyOrderMutation();

  const operationTypes = [PeOrderOperationType.Sell, PeOrderOperationType.Buy];

  const operationText = {
    [PeOrderOperationType.Buy]: t('buy'),
    [PeOrderOperationType.Sell]: t('sell'),
  };

  const currencySymbol = companyCurrency ?? '$';

  const sharedValidation = Yup.number().required(t('fieldIsRequired') ?? '').min(0, t('minNumValue', { amount: 0 }) ?? '');

  const validationSchema = Yup.object().shape({
    quantity: sharedValidation,
    requested_price: sharedValidation,
    sum: sharedValidation,
  });

  return (
    <>
      <GridHeading>{companyName}</GridHeading>
      <AppCard p="40px">
        <Formik<FormValues>
          initialValues={{
            quantity: quantity?.toString() ?? '',
            operation: operationType ?? PeOrderOperationType.Buy,
            requested_price: requestedPrice?.toString() ?? '',
            sum:
              quantity && requestedPrice
                ? (quantity * requestedPrice).toString()
                : '',
          }}
          validationSchema={validationSchema}
          onSubmit={(values, formik) => {
            createPeOrder({
              company_id: companyId,
              quantity: values.quantity.trim() ? +values.quantity : null,
              operation: values.operation,
              requested_price: values.requested_price.trim()
                ? +values.requested_price
                : null,
              sum: values.sum.trim() ? +values.sum : null,
            })
              .unwrap()
              .then(() => {
                toast({
                  description: t('peOrderCreated'),
                  status: 'success',
                  position: 'top-right',
                  isClosable: true,
                });
                navigate(AppRoute.peSelfOrders);
              })
              .catch((err) => formErrorHandler(err, formik));
          }}
        >
          {({ values, setFieldValue }) => {
            return (
              <Form>
                <Box mb="40px">
                  <FormLabel>{t('peCreateOrderOperationLabel')}</FormLabel>
                  <ButtonTabs
                    onChange={(index) =>
                      setFieldValue('operation', operationTypes[index])
                    }
                    index={operationTypes.indexOf(values.operation)}
                    items={operationTypes.map(
                      (period) => operationText[period]
                    )}
                    tabItemProps={{
                      flex: 1,
                    }}
                  />
                </Box>
                <Box mb="40px">
                  <FormNumberInput
                    name="quantity"
                    onChange={(val) => {
                      setFieldValue('quantity', val);
                      if (values.requested_price.trim()) {
                        setFieldValue(
                          'sum',
                          val ? (+val * +values.requested_price).toString() : ''
                        );
                      }
                    }}
                    value={values.quantity}
                    label={t('sharesQuantity')}
                  />
                </Box>
                <Box mb="40px">
                  <FormNumberInput
                    name="requested_price"
                    onChange={(val) => {
                      setFieldValue('requested_price', val);
                      if (values.quantity.trim()) {
                        setFieldValue(
                          'sum',
                          val ? (+val * +values.quantity).toString() : ''
                        );
                      }
                    }}
                    value={values.requested_price}
                    label={t('sharesPriceLabel', {
                      currency: currencySymbol,
                    })}
                  />
                </Box>
                <Box mb="40px">
                  <FormNumberInput
                    name="sum"
                    onChange={(val) => {
                      setFieldValue('sum', val);
                      if (values.requested_price.trim()) {
                        setFieldValue(
                          'quantity',
                          val
                            ? Math.floor(
                                +val / +values.requested_price
                              ).toString()
                            : ''
                        );
                      }
                    }}
                    value={values.sum}
                    label={t(
                      values.operation === PeOrderOperationType.Buy
                        ? 'sharesBuyVolumeLabel'
                        : 'sharesSellVolumeLabel',
                      {
                        currency: currencySymbol,
                      }
                    )}
                  />
                </Box>
                <Button w="full" type="submit" isLoading={isLoading}>
                  {t('peMakeRequest')}
                </Button>
              </Form>
            );
          }}
        </Formik>
      </AppCard>
    </>
  );
};

export default CreatePeCompanyOrderForm;
